import { useLanguage, SUPPORTED_LANGUAGES } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel
} from '@/components/ui/dropdown-menu';
import { Globe, Check } from 'lucide-react';

export default function LanguageSwitcher({ variant = 'ghost', size = 'sm', showLabel = true, alwaysShowFull = false, className = '' }) {
  const { language, setLanguage, getCurrentLanguage, t } = useLanguage();
  const currentLang = getCurrentLanguage();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={variant}
          size={size}
          className={`flex items-center gap-2 ${className}`}
          data-testid="language-switcher-btn"
        >
          <Globe className="w-4 h-4" />
          {showLabel && (
            <>
              <span className={`font-medium ${alwaysShowFull ? '' : 'hidden sm:inline'}`}>
                {currentLang.nativeName}
              </span>
              {!alwaysShowFull && (
                <span className="font-medium sm:hidden text-xs">
                  {currentLang.code?.toUpperCase()}
                </span>
              )}
            </>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuLabel className="text-xs text-muted-foreground">
          {t('languageSwitcher.title') || 'Select Language'}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {SUPPORTED_LANGUAGES.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => setLanguage(lang.code)}
            className={`flex items-center justify-between cursor-pointer ${
              language === lang.code ? 'bg-accent' : ''
            }`}
            data-testid={`language-option-${lang.code}`}
          >
            <div className="flex items-center gap-2">
              <span className="text-base">{lang.flag}</span>
              <div className="flex flex-col">
                <span className="font-medium">{lang.nativeName}</span>
                {lang.code !== 'en' && (
                  <span className="text-xs text-muted-foreground">{lang.name}</span>
                )}
              </div>
            </div>
            {language === lang.code && (
              <Check className="w-4 h-4 text-primary" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Compact version for mobile
export function LanguageSwitcherCompact({ className = '' }) {
  const { language, setLanguage, getCurrentLanguage } = useLanguage();
  const currentLang = getCurrentLanguage();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={`h-8 gap-1.5 ${className}`}
          data-testid="language-switcher-compact"
        >
          <Globe className="w-4 h-4" />
          <span className="text-xs font-medium">{currentLang.nativeName}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-40">
        {SUPPORTED_LANGUAGES.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => setLanguage(lang.code)}
            className={`flex items-center gap-2 cursor-pointer ${
              language === lang.code ? 'bg-accent' : ''
            }`}
          >
            <span>{lang.flag}</span>
            <span className="font-medium">{lang.nativeName}</span>
            {language === lang.code && (
              <Check className="w-3 h-3 ml-auto text-primary" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
